import { Children, cloneElement, isValidElement, ReactElement, ReactNode } from 'react';

import { swapNationalPhone } from '@aspendental/web-shared-utils';

/**
 * Recursively swap the national call-center number for the Patient Prism pool
 * number in every string leaf of a React children tree.
 *
 * `swapNationalPhone` handles plain display text (e.g. `(888) 486-3443`) via its
 * free-text path, so callers that wrap the number in markup
 * (`<strong>(888) 486-3443</strong>`, fragments, arrays) still have the visible
 * label swapped — keeping it in sync with the swapped `tel:` href. Non-string,
 * non-element nodes are returned untouched.
 *
 * Only call this once the `ccPatientPrismPhoneSwap` swap has been gated on by the
 * caller; the swap is applied unconditionally here.
 */
export const swapPhoneNumberInChildren = (children: ReactNode): ReactNode => {
	if (typeof children === 'string') {
		return swapNationalPhone(children, true);
	}

	if (Array.isArray(children)) {
		return Children.map(children, swapPhoneNumberInChildren);
	}

	if (isValidElement(children)) {
		const element = children as ReactElement<{ children?: ReactNode }>;
		if (element.props?.children !== undefined) {
			return cloneElement(element, {
				...element.props,
				children: swapPhoneNumberInChildren(element.props.children),
			});
		}
	}

	return children;
};
